import { useTranslation } from 'react-i18next';

import { TextAreaField, TextField } from '../../components/Field';
import type { CustomFieldDefinition } from './api';
import { CustomFieldSuggestInput } from './CustomFieldSuggestInput';
import { Select } from '../../components/Select';

interface CustomFieldInputsProps {
  definitions: CustomFieldDefinition[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}

/**
 * Die Eingabefelder fuer die eigenen Felder eines Objekts.
 *
 * Wiederverwendbar, weil dieselben Felder am Ticket, am Kunden und am Kontakt
 * erscheinen. Die Pruefung liegt im Backend -- hier wird nur das passende
 * Bedienelement gewaehlt und der Wert im richtigen Typ weitergereicht.
 *
 * Der Typ ist wichtig: Eine Zahl als Zeichenkette zu senden waere ein Fehler,
 * den der Server zu Recht abweist. Die Umwandlung geschieht deshalb hier und
 * nicht erst beim Absenden.
 *
 * **Feldabhaengigkeiten -- F3-16.** Ein Feld, dessen Bedingung nicht zutrifft,
 * wird ausgeblendet **und sein Wert geleert**. Das Ausblenden allein waere zu
 * wenig: Der Server nimmt einen Wert an einem Feld, das nicht gilt, nicht an
 * (422) -- ein unsichtbares Feld mit altem Inhalt liesse das Formular also
 * scheitern, ohne dass der Anwender sehen koennte, woran.
 *
 * Die Entscheidung, **welche** Felder gelten, faellt trotzdem im Backend:
 * ``dependencies.py`` ist die eine Umsetzung, hier steht ihre Spiegelung fuer
 * die Anzeige. Zwei Umsetzungen einer Regel laufen auseinander -- deshalb ist
 * diese hier die Bequemlichkeit und jene die Wahrheit.
 */

/** Wie ein Wert in einer Bedingung verglichen wird -- wie im Backend. */
function normalise(value: unknown): string[] {
  if (value === null || value === undefined) return [];
  if (typeof value === 'boolean') return [value ? 'true' : 'false'];
  if (Array.isArray(value)) return value.flatMap(normalise);
  if (typeof value === 'number' || typeof value === 'string') return [String(value)];

  // Ein Objekt kommt in einem eigenen Feld nicht vor -- der Server nimmt
  // nichts dergleichen an. Es hier stillschweigend zu "[object Object]" zu
  // machen waere trotzdem falsch: Dann traefe eine Bedingung zufaellig zu.
  return [];
}

/**
 * Gilt dieses Feld bei den aktuellen Werten?
 *
 * Ketten werden mitgerechnet: Eine Bedingung an einem Feld, das selbst nicht
 * gilt, kann nicht zutreffen. Die Tiefe ist begrenzt -- ein Kreis wird beim
 * Speichern abgewiesen, aber die Anzeige soll auch dann nicht haengen, wenn
 * doch einer im Bestand steht.
 */
function applies(
  definition: CustomFieldDefinition,
  all: CustomFieldDefinition[],
  values: Record<string, unknown>,
  depth = 0,
): boolean {
  if (!definition.depends_on_key) return true;
  if (depth >= 5) return false;

  const controlling = all.find((entry) => entry.key === definition.depends_on_key);
  if (!controlling) return true;
  if (!applies(controlling, all, values, depth + 1)) return false;

  const actual = new Set(normalise(values[controlling.key]));
  return (definition.depends_on_values ?? []).some((wanted) => actual.has(wanted));
}

export function CustomFieldInputs({ definitions, values, onChange }: CustomFieldInputsProps) {
  const { t } = useTranslation();

  const applicable = definitions.filter((definition) => applies(definition, definitions, values));

  // Was gerade nicht gilt, aber noch einen Wert traegt, wird geleert -- siehe
  // Modulkopf. Das geschieht beim Zeichnen und nicht in einem Effekt: Ein
  // Effekt liefe erst nach der Anzeige, und dazwischen stuende ein Formular,
  // das der Server ablehnen wuerde.
  const stale = definitions.filter(
    (definition) =>
      !applicable.includes(definition) &&
      values[definition.key] !== undefined &&
      values[definition.key] !== '',
  );
  if (stale.length > 0) {
    queueMicrotask(() => {
      stale.forEach((definition) => {
        onChange(definition.key, undefined);
      });
    });
  }

  if (applicable.length === 0) return null;

  return (
    <>
      {applicable.map((definition) => {
        const value = values[definition.key];
        const label = definition.is_required ? `${definition.label} *` : definition.label;
        const hint = definition.help_text || undefined;

        switch (definition.field_type) {
          case 'textarea':
            return (
              <TextAreaField
                key={definition.id}
                label={label}
                hint={hint}
                rows={3}
                value={typeof value === 'string' ? value : ''}
                onChange={(event) => {
                  onChange(definition.key, event.target.value);
                }}
              />
            );

          case 'boolean':
            return (
              <div className="field" key={definition.id}>
                <label className="row" style={{ gap: 'var(--space-2)' }}>
                  <input
                    type="checkbox"
                    checked={value === true}
                    onChange={(event) => {
                      onChange(definition.key, event.target.checked);
                    }}
                  />
                  <span className="field__label" style={{ margin: 0 }}>
                    {label}
                  </span>
                </label>
                {hint ? <span className="field__hint">{hint}</span> : null}
              </div>
            );

          case 'select':
            return (
              <div className="field" key={definition.id}>
                <label className="field__label" htmlFor={`cf-${definition.id}`}>
                  {label}
                </label>
                <Select
                  id={`cf-${definition.id}`}
                  className="field__control"
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) => {
                    onChange(definition.key, event.target.value || null);
                  }}
                >
                  <option value="">{t('app.none')}</option>
                  {definition.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
                {hint ? <span className="field__hint">{hint}</span> : null}
              </div>
            );

          case 'multiselect':
            return (
              <div className="field" key={definition.id}>
                <span className="field__label">{label}</span>
                {definition.options.map((option) => {
                  const selected = Array.isArray(value) ? (value as string[]) : [];
                  return (
                    <label key={option} className="row" style={{ gap: 'var(--space-2)' }}>
                      <input
                        type="checkbox"
                        checked={selected.includes(option)}
                        onChange={(event) => {
                          onChange(
                            definition.key,
                            event.target.checked
                              ? [...selected, option]
                              : selected.filter((entry) => entry !== option),
                          );
                        }}
                      />
                      <span>{option}</span>
                    </label>
                  );
                })}
                {hint ? <span className="field__hint">{hint}</span> : null}
              </div>
            );

          case 'text':
            // Mit Vorschlaegen aus dem Bestand (F1-49, Etappe 3): Wer
            // "HP La" tippt, soll das Modell so schreiben, wie es schon
            // vierzigmal dasteht.
            return (
              <div className="field" key={definition.id}>
                <label className="field__label" htmlFor={`custom-field-${definition.id}`}>
                  {label}
                </label>
                <CustomFieldSuggestInput
                  id={`custom-field-${definition.id}`}
                  definitionId={definition.id}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(next) => {
                    onChange(definition.key, coerce(definition.field_type, next));
                  }}
                />
                {hint ? <span className="field__hint">{hint}</span> : null}
              </div>
            );

          default:
            return (
              <TextField
                key={definition.id}
                label={label}
                hint={hint}
                type={inputTypeFor(definition.field_type)}
                inputMode={definition.field_type === 'decimal' ? 'decimal' : undefined}
                value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
                onChange={(event) => {
                  onChange(definition.key, coerce(definition.field_type, event.target.value));
                }}
              />
            );
        }
      })}
    </>
  );
}

function inputTypeFor(fieldType: CustomFieldDefinition['field_type']): string {
  switch (fieldType) {
    case 'integer':
      return 'number';
    case 'decimal':
      // Ausdruecklich **kein** ``type="number"``: Waehrend der Eingabe von
      // "12.5" ist "12." ein ungueltiger Zwischenzustand, und der Browser
      // liefert dann eine leere Zeichenkette. Der Nachkommateil ginge damit
      // verloren -- bei einem Feld "Stundensatz" faellt das erst in der
      // Abrechnung auf. ``inputMode`` sorgt trotzdem fuer die Zifferntastatur
      // auf dem Mobilgeraet.
      return 'text';
    case 'date':
      return 'date';
    case 'datetime':
      return 'datetime-local';
    case 'email':
      return 'email';
    case 'url':
      return 'url';
    default:
      return 'text';
  }
}

/**
 * Bringt die Eingabe in den Typ, den der Server erwartet.
 *
 * Ein leeres Feld wird zu ``null`` -- das ist im Backend das Zeichen fuer
 * "leeren", nicht fuer "unveraendert lassen".
 */
function coerce(fieldType: CustomFieldDefinition['field_type'], raw: string): unknown {
  if (raw === '') return null;

  switch (fieldType) {
    case 'integer': {
      const parsed = Number.parseInt(raw, 10);
      return Number.isNaN(parsed) ? raw : parsed;
    }
    case 'decimal':
      // Als Zeichenkette weiterreichen: Der Server rechnet mit Decimal, und
      // eine Fliesskommazahl verloere hier schon Nachkommastellen.
      return raw;
    case 'datetime':
      // ``datetime-local`` liefert einen Zeitpunkt ohne Zone. Der Server
      // deutet ihn als UTC -- was hier ausdruecklich gemacht wird, statt es
      // dem Zufall zu ueberlassen.
      return new Date(raw).toISOString();
    default:
      return raw;
  }
}
